import type {
  ActiveTemplateField,
  ExtractionReviewField,
  LocalizedTableColumn,
  ProfileFieldValidation,
} from '../../api/types';
import { asTableRows } from '../../organization-profile/utils/fieldValue';

/** Turn a raw row key (e.g. "retention_period") into a readable header. */
function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * The extraction payload carries table data as rows but no column schema (that
 * lives in the catalog, which the review endpoint doesn't return). Reconstruct
 * the columns from the union of keys across the extracted rows so the shared
 * `TableGridField` — which renders strictly from `field.columns` — has headers
 * to show instead of an empty grid.
 */
function deriveTableColumns(value: unknown): LocalizedTableColumn[] {
  const keys: string[] = [];
  for (const row of asTableRows(value)) {
    for (const key of Object.keys(row)) {
      if (!keys.includes(key)) keys.push(key);
    }
  }
  return keys.map((key, orderIndex) => ({
    key,
    type: 'string',
    label: humanizeKey(key),
    description: null,
    isRequired: false,
    validation: null,
    options: null,
    orderIndex,
  }));
}

/**
 * Adapts an `ExtractionReviewField` (the review payload shape) to the
 * `ActiveTemplateField` the shared field editors expect. The editors only read
 * `type` / `options` / `validation` / `label` / `columns`, so the
 * value/confidence/citation provenance is supplied separately by the review
 * card — those fields are zeroed here.
 */
export function reviewFieldToTemplateField(
  field: ExtractionReviewField,
  /**
   * Column schema from the tenant-profile catalog (the source of truth). When
   * provided it's used as-is so a table renders its columns even with no
   * extracted rows; only when the catalog is unavailable do we fall back to
   * reconstructing columns from the extracted rows.
   */
  catalogColumns?: LocalizedTableColumn[] | null,
): ActiveTemplateField {
  // `label` arrives already localized from the API; fall back to the key only
  // when the template field has no label at all.
  const label = field.label || field.key;

  const columns =
    field.type === 'table'
      ? (catalogColumns?.length ? catalogColumns : deriveTableColumns(field.aiValue))
      : null;

  return {
    id: field.fieldId,
    key: field.key,
    type: field.type,
    label,
    validation: (field.validation as ProfileFieldValidation | null) ?? null,
    example: null,
    isRequired: field.isRequired,
    columns,
    options: field.options,
    currentValue: null,
    lastEditedByAi: null,
    confidence: null,
    sourceCitation: null,
  };
}
